/* eslint-disable max-lines */
import { Box, Button, Divider, Stack, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { type ReactElement, useState } from "react";

import { environmentConfig } from "../environment";
import { AppBarHeader, BackButtonLink, PageWithHeader, useToast } from "../lib";
import { DebugCards } from "./Cards/Cards";
import { DebugErrorPage } from "./DebugErrorPage";
import { useMobileConsole } from "./MobileConsole";
import { NativePlugins } from "./NativePlugins/NativePlugins";
import { NfcPluginDebugger } from "./NativePlugins/NfcPluginDebugger";
import { NfcScannerDebugPage } from "./Pages/NfcScannerDebugPage";

export function DebugPage(): ReactElement {
  const { showErrorToast, showSuccessToast, showInfoToast, showWarningToast } = useToast();
  const { isVisible, toggleVisibility } = useMobileConsole();

  const [developmentToolsEnabled, setDevelopmentToolsEnabled] = useState(
    () => (localStorage.getItem("areDevToolsVisible") ?? "true") === "true"
  );

  const toggleDevelopmentTools = () => {
    const newValue = !developmentToolsEnabled;
    setDevelopmentToolsEnabled(newValue);
    localStorage.setItem("areDevToolsVisible", newValue.toString());
    // Show toast to inform user about reload requirement
    showInfoToast("Development tools setting changed. Reload the page to apply changes.");
  };

  const TOASTS = {
    error: showErrorToast,
    info: showInfoToast,
    warning: showWarningToast,
    success: showSuccessToast,
  };
  const [shouldThrowError, setShouldThrowError] = useState(false);

  const environmentVariables = [
    { name: "REACT_APP_ENVIRONMENT_NAME", value: environmentConfig.REACT_APP_ENVIRONMENT_NAME },
    { name: "REACT_APP_VERSION_NAME", value: environmentConfig.REACT_APP_VERSION_NAME ?? "" },
    {
      name: "REACT_APP_BITRISE_BUILD_NUMBER",
      value: environmentConfig.REACT_APP_BITRISE_BUILD_NUMBER ?? "???",
    },
    {
      name: "REACT_APP_DATADOG_RUM_SOURCEMAP_VERSION",
      value: environmentConfig.REACT_APP_DATADOG_RUM_SOURCEMAP_VERSION ?? "???",
    },
  ];

  return (
    <PageWithHeader
      appBarHeader={
        <AppBarHeader
          title="Debug Page"
          leftCta={<BackButtonLink defaultBackTo={`${RootPaths.HOME}/account`} />}
        />
      }
      containerVariant="without-margin"
    >
      <Stack spacing={2} sx={{ padding: 2 }}>
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Native Plugins
          </Title>
          <NativePlugins />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Mobile Console
          </Title>
          <Stack spacing={2}>
            <Text>
              Mobile console is {isVisible ? "visible" : "hidden"}. Use these controls or browser
              console commands.
            </Text>
            <Button variant="contained" onClick={toggleVisibility}>
              {isVisible ? "Hide" : "Show"} Console
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                console.log("Test log from debug page!");
              }}
            >
              Test Log
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                console.warn("Test warning!");
              }}
            >
              Test Warning
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                console.error("Test error!");
              }}
            >
              Test Error
            </Button>
            <Text variant="body2" sx={{ fontStyle: "italic", textAlign: "center" }}>
              Browser console commands: window.cbhMobileConsole.show(), .hide(), .toggle()
            </Text>
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Development Tools
          </Title>
          <Stack spacing={2}>
            <Text>
              Development tools are {developmentToolsEnabled ? "enabled" : "disabled"}. Changes
              require a page reload.
            </Text>
            <Text variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
              Controls React Query DevTools and other debugging utilities
            </Text>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="contained" onClick={toggleDevelopmentTools}>
                {developmentToolsEnabled ? "Disable" : "Enable"} DevTools
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  globalThis.location.reload();
                }}
              >
                Reload Page
              </Button>
            </Stack>
            <Text variant="body2" sx={{ fontStyle: "italic", textAlign: "center" }}>
              DevTools appear as floating icons when enabled (development only)
            </Text>
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Environment Variables
          </Title>
          <Table>
            <TableBody>
              {environmentVariables.map((environmentVariable) => (
                <TableRow key={environmentVariable.name}>
                  <TableCell>
                    {environmentVariable.name}: <b>{environmentVariable.value}</b>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <Text variant="h2" color="error">
            Testing error color override. If this is black, then the `cbhAppV1` class is overriding
            headings.
          </Text>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Toasts
          </Title>
          <Stack spacing={2}>
            {Object.keys(TOASTS).map((toastName) => {
              return (
                <Button
                  key={toastName}
                  variant="contained"
                  onClick={() => {
                    TOASTS[toastName as keyof typeof TOASTS](
                      `You clicked ${toastName}. This is a very long toast, so check it out.`
                    );
                  }}
                >
                  Show {toastName}
                </Button>
              );
            })}
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Error Logging
          </Title>
          <Stack spacing={2}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                throw new Error(`Debugger Error for Frontend Testing.`);
              }}
            >
              Throw an Error
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setShouldThrowError(true);
              }}
            >
              Render an Error Component
            </Button>
          </Stack>
          <DebugErrorPage shouldThrowError={shouldThrowError} />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            NFC Debuggers
          </Title>
          <Stack spacing={1} mt={1}>
            <NfcPluginDebugger />
            <NfcScannerDebugPage />
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Authentication Testing
          </Title>
          <DebugCards
            cards={[
              {
                id: 1,
                content: <Text>🔒 Cognito Magic Link Test</Text>,
                to: "/v2/cognito-test",
              },
            ]}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Title component="h3" sx={{ textAlign: "center" }}>
            Route Debugging section
          </Title>
          <DebugCards
            cards={[
              {
                id: 1,
                content: <Text>Open Shifts</Text>,
                to: "/home/openShifts",
              },
              {
                id: 2,
                content: <Text>Pull To Refresh Debugger</Text>,
                to: "/home-v2/debug/pull-to-refresh",
              },
              {
                id: 3,
                content: <Text>📱 Screen Brightness Debugger</Text>,
                to: "/home-v2/debug/screen-brightness",
              },
            ]}
          />
        </Box>
      </Stack>
    </PageWithHeader>
  );
}
/* eslint-enable max-lines */
